import React, { useState } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../index";
import Spinner from "../spinner";

const ADMIN_PASSWORD = "ohadadmin"; // Change this to your secure password

const AdminSiumViewer = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState("");
    const [siumim, setSiumim] = useState([]);
    const [siumimAher, setSiumimAher] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("siumim");
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedSium, setExpandedSium] = useState(null);
    const [migrationInProgress, setMigrationInProgress] = useState(true);
    const [migrationResult, setMigrationResult] = useState(null);

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
            fetchAllSiumim();
        } else {
            alert("סיסמה שגויה");
        }
    };

    // One-time normalization function - run manually (then remove this code)
    const normalizeDatabase = async () => {
        if (!window.confirm('האם להפעיל נירמול חד-פעמי של אימיילים ומספרי טלפון במסמכי ה-DB? פעולה זו ממירה אימייל ל-lowercase ו-trim, ומסירה תווים לא-ספרתיים מהטלפון.')) return;
        setMigrationInProgress(true);
        setMigrationResult(null);
        setLoading(true);
        let siumUpdated = 0;
        let usersUpdated = 0;
        const errors = [];

        try {
            // Normalize siumim collection
            const siumimSnapshot = await getDocs(collection(db, 'siumim'));
            for (const d of siumimSnapshot.docs) {
                try {
                    const data = d.data() || {};
                    const updates = {};
                    if (data.organizerEmail) {
                        const newEmail = String(data.organizerEmail).trim().toLowerCase();
                        if (newEmail !== data.organizerEmail) updates.organizerEmail = newEmail;
                    }
                    if (data.organizerPhone) {
                        const newPhone = String(data.organizerPhone).replace(/\D/g, '');
                        if (newPhone !== data.organizerPhone) updates.organizerPhone = newPhone;
                    }
                    if (Object.keys(updates).length > 0) {
                        await updateDoc(doc(db, 'siumim', d.id), updates);
                        siumUpdated++;
                    }
                } catch (e) {
                    console.error('Error updating siumim doc', d.id, e);
                    errors.push({ collection: 'siumim', id: d.id, error: String(e) });
                }
            }

            // Normalize users collection
            const usersSnapshot = await getDocs(collection(db, 'users'));
            for (const d of usersSnapshot.docs) {
                try {
                    const data = d.data() || {};
                    const updates = {};
                    if (data.email) {
                        const newEmail = String(data.email).trim().toLowerCase();
                        if (newEmail !== data.email) updates.email = newEmail;
                    }
                    if (data.phone) {
                        const newPhone = String(data.phone).replace(/\D/g, '');
                        if (newPhone !== data.phone) updates.phone = newPhone;
                    }
                    if (Object.keys(updates).length > 0) {
                        await updateDoc(doc(db, 'users', d.id), updates);
                        usersUpdated++;
                    }
                } catch (e) {
                    console.error('Error updating users doc', d.id, e);
                    errors.push({ collection: 'users', id: d.id, error: String(e) });
                }
            }

            setMigrationResult(`נירמול הושלם: ${siumUpdated} סיומים עודכנו, ${usersUpdated} משתמשים עודכנו.` + (errors.length ? ` יש ${errors.length} שגיאות (בדוק קונסול).` : ''));
        } catch (e) {
            console.error('Normalization failed', e);
            setMigrationResult('אירעה שגיאה כללית בנירמול - בדוק קונסול');
        } finally {
            setMigrationInProgress(false);
            setLoading(false);
        }
    };

    const fetchAllSiumim = async () => {
        setLoading(true);
        try {
            // Fetch from siumim collection
            const siumimSnapshot = await getDocs(collection(db, "siumim"));
            const siumimData = siumimSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setSiumim(siumimData);

            // Fetch from siumimAher collection
            const siumimAherSnapshot = await getDocs(collection(db, "siumimAher"));
            const siumimAherData = siumimAherSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setSiumimAher(siumimAherData);
        } catch (error) {
            console.error("Error fetching siumim:", error);
            alert("שגיאה בטעינת הנתונים");
        }
        setLoading(false);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "לא זמין";
        try {
            if (timestamp.toDate) {
                return timestamp.toDate().toLocaleDateString('he-IL');
            }
            if (timestamp.seconds) {
                return new Date(timestamp.seconds * 1000).toLocaleDateString('he-IL');
            }
            return new Date(timestamp).toLocaleDateString('he-IL');
        } catch {
            return "לא זמין";
        }
    };

    const formatValue = (value, key) => {
        if (value === null || value === undefined) return "לא זמין";
        if (typeof value === 'boolean') return value ? "כן" : "לא";

        // Handle millisecond timestamps for specific fields
        const dateFields = ['created', 'startSium', 'endSium', 'lastAccessed'];
        if (dateFields.includes(key) && typeof value === 'number') {
            return new Date(value).toLocaleDateString('he-IL');
        }

        if (typeof value === 'object') {
            if (value.toDate) return formatDate(value);
            if (value.seconds) return formatDate(value);
            if (Array.isArray(value)) return `[מערך - ${value.length} פריטים]`;
            return "[אובייקט]";
        }
        return String(value);
    };

    const filterSiumim = (data) => {
        if (!searchQuery.trim()) return data;
        const query = searchQuery.toLowerCase();
        return data.filter(sium =>
            sium.kehilaName?.toLowerCase().includes(query) ||
            sium.organizerEmail?.toLowerCase().includes(query) ||
            sium.organizerPhone?.includes(query) ||
            sium.id?.toLowerCase().includes(query)
        );
    };

    const renderSiumDetails = (sium) => {
        const excludeKeys = ['masehtotSelection', 'mipuiId']; // Large objects to exclude from details
        return (
            <div className="sium-details-expanded">
                <table className="table table-sm table-bordered">
                    <tbody>
                        {Object.entries(sium)
                            .filter(([key]) => !excludeKeys.includes(key))
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([key, value]) => (
                                <tr key={key}>
                                    <td className="fw-bold" style={{ width: '30%' }}>{key}</td>
                                    <td>{formatValue(value, key)}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderSiumCard = (sium, index) => {
        const isExpanded = expandedSium === sium.id;
        return (
            <div key={sium.id} className="card mb-3">
                <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedSium(isExpanded ? null : sium.id)}
                >
                    <div>
                        <strong>{index + 1}. {sium.kehilaName || "ללא שם"}</strong>
                        <span className="badge bg-secondary ms-2">{sium.id}</span>
                    </div>
                    <div>
                        {sium.organizerEmail && <span className="badge bg-info me-2">{sium.organizerEmail}</span>}
                        <span className="badge bg-primary">{isExpanded ? "▲" : "▼"}</span>
                    </div>
                </div>
                {isExpanded && (
                    <div className="card-body">
                        {renderSiumDetails(sium)}
                    </div>
                )}
            </div>
        );
    };

    if (!isAuthenticated) {
        return (
            <div className="sium-main">
                <div className="sium-main-container">
                    <div className="basad">
                        <span>בס"ד</span>
                        <br />
                        <span>להצלחת עם ישראל</span>
                    </div>
                    <div className="sium-logo">
                        <div className="sium-logo-img" onClick={() => window.location = '/'} style={{cursor: 'pointer'}} aria-label="Main page" role="button"></div>
                    </div>
                    <div className="grid p-3 text-primary-emphasis bg-primary-subtle border border-primary-subtle rounded-3 sium-titles">
                        <h2>ניהול מערכת - צפייה בסיומים</h2>
                        <h4>כניסת מנהל</h4>
                    </div>
                    <form onSubmit={handleLogin} className="mt-4">
                        <div className="mb-3">
                            <input
                                type="password"
                                className="form-control"
                                placeholder="הזן סיסמת מנהל"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn sium-button">כניסה</button>
                    </form>
                </div>
            </div>
        );
    }

    if (loading) {
        return <Spinner />;
    }

    const currentData = activeTab === "siumim" ? siumim : siumimAher;
    const filteredData = filterSiumim(currentData);

    return (
        <div className="sium-main">
            <div className="sium-main-container">
                <div className="basad">
                    <span>בס"ד</span>
                    <br />
                    <span>להצלחת עם ישראל</span>
                </div>
                <div className="sium-logo">
                    <div className="sium-logo-img" onClick={() => window.location = '/'} style={{cursor: 'pointer'}} aria-label="Main page" role="button"></div>
                </div>
                <div className="grid p-3 text-primary-emphasis bg-primary-subtle border border-primary-subtle rounded-3 sium-titles">
                    <h2>ניהול מערכת - צפייה בסיומים</h2>
                    <p className="mb-0">
                        סה"כ: {siumim.length} סיומי ש"ס | {siumimAher.length} סיומי ספרים אחרים
                    </p>
                </div>

                {/* One-time migration control (admin) */}
                <div className="mb-3">
                    <button
                        className="btn btn-danger"
                        onClick={normalizeDatabase}
                        disabled={migrationInProgress}
                    >
                        {migrationInProgress ? 'מריץ נירמול...' : 'נירמול אימיילים וטלפונים (הרץ פעם אחת)'}
                    </button>
                    {migrationResult && <div className="mt-2 alert alert-info" role="status">{migrationResult}</div>}
                </div>

                {/* Tabs */}
                <div className="btn-group w-100 mt-3 mb-3" role="group">
                    <button
                        type="button"
                        className={`btn ${activeTab === 'siumim' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setActiveTab('siumim')}
                    >
                        סיומי ש"ס ({siumim.length})
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'siumimAher' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setActiveTab('siumimAher')}
                    >
                        סיומי ספרים אחרים ({siumimAher.length})
                    </button>
                </div>

                {/* Search */}
                <div className="mb-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="חיפוש לפי שם, אימייל, טלפון או ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* Results count */}
                <p className="text-muted">
                    מציג {filteredData.length} תוצאות
                    {searchQuery && ` (מסונן מתוך ${currentData.length})`}
                </p>

                {/* Sium cards */}
                <div className="siumim-list">
                    {filteredData.map((sium, index) => renderSiumCard(sium, index))}
                </div>

                {filteredData.length === 0 && (
                    <div className="alert alert-info">
                        לא נמצאו תוצאות
                    </div>
                )}

                <div className="sium-bottom-page margin-top-20">
                    <button className="btn btn-secondary mb-3" onClick={() => setIsAuthenticated(false)}>
                        התנתק
                    </button>
                    <p><span> © כל הזכויות שמורות לאתר סיום הש"ס</span></p>
                </div>
            </div>
        </div>
    );
};

export default AdminSiumViewer;

